import type { Message } from '@chatapp-inbox/shared';
import { dayKey, formatDayLabel } from './format';
import { localId, type LocalSend } from './outbox';

export interface DayEntry {
  kind: 'day';
  key: string;
  label: string;
}

export interface MessageEntry {
  kind: 'message';
  /** Identidade estável do item na tela (id do servidor ou `local:<clientId>`). */
  key: string;
  message: Message;
  /** Preenchido nos envios desta aba ainda não confirmados pelo servidor. */
  local: LocalSend | null;
}

export type TimelineEntry = DayEntry | MessageEntry;

export interface Viewer {
  id: string;
  name: string;
}

export interface Timeline {
  entries: TimelineEntry[];
  /** Envios sem confirmação que o servidor, na verdade, já tem: podem sair da fila local. */
  settled: string[];
}

/** O envio local como uma mensagem de saída, para a tela tratar os dois do mesmo jeito. */
function localAsMessage(local: LocalSend, conversationId: string, viewer: Viewer): Message {
  return {
    id: localId(local.clientId),
    conversationId,
    direction: 'out',
    text: local.text,
    status: local.state === 'queued' || local.state === 'sending' ? 'pending' : 'error',
    errorCode: null,
    authorUserId: viewer.id,
    authorName: viewer.name,
    createdAt: local.createdAt,
    attachment: local.attachment
      ? {
          id: localId(local.clientId),
          kind: local.attachment.kind,
          mimeType: local.attachment.mimeType,
          fileName: local.attachment.file.name,
          sizeBytes: local.attachment.file.size,
          status: 'uploaded',
          voice: false,
        }
      : null,
    template: null,
  };
}

/**
 * A mensagem do servidor que corresponde a este envio local, se ela já chegou (por refetch ou,
 * na T26, por evento do tempo real) antes de o pedido terminar. A API não devolve o `clientId`, então
 * o reconhecimento é: saída desta pessoa, mesmo texto, mais nova que tudo o que o cache tinha
 * quando o envio nasceu e ainda não reivindicada por outro envio. Só vale para envios que de fato
 * saíram (`sending`/`unconfirmed`): um que ainda espera a vez não pode ter chegado.
 */
function findArrival(
  local: LocalSend,
  server: readonly Message[],
  viewerId: string,
  claimed: Set<string>,
): Message | undefined {
  if (local.state !== 'sending' && local.state !== 'unconfirmed') return undefined;
  const after = local.after === null ? -Infinity : Date.parse(local.after);
  return server.find(
    (m) =>
      !claimed.has(m.id) &&
      m.direction === 'out' &&
      m.authorUserId === viewerId &&
      m.text === local.text &&
      // Com arquivo, o que chegou tem de ter arquivo do mesmo tipo e nome; sem arquivo, não pode ter.
      (local.attachment
        ? m.attachment?.kind === local.attachment.kind &&
          m.attachment.fileName === local.attachment.file.name
        : !m.attachment) &&
      Date.parse(m.createdAt) > after,
  );
}

/**
 * Junta as mensagens do servidor (ordem cronológica) com os envios locais e intercala os
 * separadores de dia. Os envios locais ficam sempre no fim, na ordem em que foram feitos: ainda não
 * têm lugar na linha do tempo do servidor e o relógio do aparelho não é confiável para ordenar.
 */
export function buildTimeline(input: {
  conversationId: string;
  server: readonly Message[];
  outbox: readonly LocalSend[];
  viewer: Viewer;
  now: Date;
}): Timeline {
  const { conversationId, server, outbox, viewer, now } = input;
  const claimed = new Set<string>();
  const settled: string[] = [];
  const pending: MessageEntry[] = [];

  for (const local of outbox) {
    const arrival = findArrival(local, server, viewer.id, claimed);
    if (arrival) {
      claimed.add(arrival.id);
      if (local.state === 'unconfirmed') settled.push(local.clientId);
      continue; // já está na lista pelo servidor; mostrar os dois duplicaria a mensagem
    }
    pending.push({
      kind: 'message',
      key: localId(local.clientId),
      message: localAsMessage(local, conversationId, viewer),
      local,
    });
  }

  const entries: TimelineEntry[] = [];
  let lastDay = '';
  // O dia nunca anda para trás: o relógio do aparelho (envios locais) pode estar atrasado em
  // relação ao do servidor e criaria um separador "Ontem" no meio da conversa de hoje.
  let effective = -Infinity;
  const push = (entry: MessageEntry): void => {
    const time = Date.parse(entry.message.createdAt);
    if (!Number.isNaN(time)) effective = Math.max(effective, time);
    if (effective !== -Infinity) {
      const date = new Date(effective);
      const key = dayKey(date);
      if (key !== lastDay) {
        lastDay = key;
        entries.push({ kind: 'day', key: `day:${key}`, label: formatDayLabel(date, now) });
      }
    }
    entries.push(entry);
  };

  for (const message of server) push({ kind: 'message', key: message.id, message, local: null });
  for (const entry of pending) push(entry);
  return { entries, settled };
}
